// DML-aware clause scanner. Handles the statement shapes that scanClause
// (SELECT-only) doesn't reach: INSERT, UPDATE, DELETE, MERGE, WITH, plus the
// PositionKinds that live inside them: insert-target, insert-values,
// update-set, update-where, delete-where, cte-def, subquery, merge-on,
// merge-when-matched, merge-when-not-matched-source.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Position Classifier — DML.
//
// Algorithm mirrors clauseScanner: walk tokens, push/pop a frame on '(' / ')',
// skip strings/comments/quoted-identifiers via shared helpers, fold multi-word
// keywords (WHEN MATCHED, WHEN NOT MATCHED BY SOURCE, ON CONFLICT DO UPDATE).
// Each frame carries the clause it would return if the cursor stopped in it;
// the outer statement kind is resolved from the first significant keyword.
// Frame + keyword-transition logic lives in ./classifyPosition.dml.frame.ts.

import type { ParseOptions } from '../../sql-grammar/model';
import type { ResolverPositionKind } from '../types';
import { skipContextFor, trySkipNonCode } from './tokenSkip';
import {
  handleKeyword,
  makeFrame,
  pushFrame,
  pushKeyword,
  type Frame,
  type OuterKind,
} from './classifyPosition.dml.frame';

export interface DmlScanResult {
  readonly clause: ResolverPositionKind;
  readonly lastCodeChar: string | null;
}

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

// eslint-disable-next-line max-lines-per-function, complexity
export function scanDmlClause(
  source: string,
  start: number,
  endExclusive: number,
  options: ParseOptions,
): DmlScanResult {
  const ctx = skipContextFor(options);
  const stack: Frame[] = [makeFrame('unknown')];
  let outerKind: OuterKind = 'unknown';
  let lastCodeChar: string | null = null;
  let i = start;

  while (i < endExclusive) {
    const skipped = trySkipNonCode(source, i, endExclusive, ctx);
    if (skipped >= 0) { i = skipped; continue; }

    const c = source[i];
    if (c === '(') {
      pushFrame(stack);
      lastCodeChar = '(';
      i++;
      continue;
    }
    if (c === ')') {
      if (stack.length > 1) stack.pop();
      lastCodeChar = ')';
      i++;
      continue;
    }
    if (/\s/.test(c)) { i++; continue; }

    if (IDENT_START.test(c)) {
      const wStart = i;
      i++;
      while (i < endExclusive && IDENT_CONT.test(source[i])) i++;
      const word = source.slice(wStart, i);
      const kw = word.toUpperCase();
      const top = stack[stack.length - 1];
      outerKind = handleKeyword(top, outerKind, kw);
      // lastWasJoin is set only on JOIN; reset when we see non-JOIN keywords
      // other than ON (which classifies as 'join-on').
      if (kw !== 'JOIN' && top.lastWasJoin && kw !== 'ON') top.lastWasJoin = false;
      pushKeyword(top, kw);
      lastCodeChar = source[i - 1];
      continue;
    }

    lastCodeChar = c;
    i++;
  }

  return { clause: stack[stack.length - 1].clause, lastCodeChar };
}
